import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Search, Edit, Phone, MapPin, Building, Calendar, User, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmployeesPage() {
  const { token, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    address: '',
    city: '',
    postal_code: '',
    chantier: '',
    emergency_contact: '',
    emergency_phone: '',
    hire_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.filter(u => u.status === 'active'));
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setForm({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      department: employee.department || '',
      position: employee.position || '',
      address: employee.address || '',
      city: employee.city || '',
      postal_code: employee.postal_code || '',
      chantier: employee.chantier || '',
      emergency_contact: employee.emergency_contact || '',
      emergency_phone: employee.emergency_phone || '',
      hire_date: employee.hire_date || '',
      notes: employee.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${selectedEmployee.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        toast.success('Informations mises à jour');
        setEditDialogOpen(false);
        fetchEmployees();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      emp.first_name?.toLowerCase().includes(searchLower) ||
      emp.last_name?.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower) ||
      emp.department?.toLowerCase().includes(searchLower) ||
      emp.chantier?.toLowerCase().includes(searchLower)
    );
  });

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Employés</h1>
          <p className="text-slate-500 mt-1">Gérez les informations des employés</p>
        </div>
        <Badge className="bg-indigo-100 text-indigo-700 text-lg px-4 py-2">
          {employees.length} employé{employees.length > 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Rechercher par nom, email, département ou chantier..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Employees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => (
          <Card key={employee.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <Avatar className="w-14 h-14">
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-lg">
                    {getInitials(employee.first_name, employee.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">
                    {employee.first_name} {employee.last_name}
                  </h3>
                  <p className="text-sm text-slate-500 truncate">{employee.email}</p>
                  {employee.position && (
                    <Badge variant="secondary" className="mt-1">{employee.position}</Badge>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(employee)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid={`edit-employee-${employee.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="mt-4 space-y-2 text-sm">
                {employee.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                {employee.department && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span>{employee.department}</span>
                  </div>
                )}
                {employee.chantier && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>Chantier: {employee.chantier}</span>
                  </div>
                )}
                {(employee.city || employee.address) && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="truncate">
                      {employee.address}{employee.address && employee.city ? ', ' : ''}{employee.city}
                    </span>
                  </div>
                )}
              </div>

              {/* Info incomplete indicator */}
              {!employee.phone && !employee.address && !employee.chantier && isAdmin && (
                <div className="mt-3 flex items-center gap-2 text-amber-600 text-xs">
                  <AlertCircle className="w-4 h-4" />
                  <span>Informations incomplètes</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Aucun employé trouvé</p>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Modifier les informations
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-6">
            {/* Informations personnelles */}
            <div>
              <h3 className="font-medium text-slate-700 mb-3">Informations personnelles</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input 
                    value={form.first_name} 
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input 
                    value={form.last_name} 
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={form.email} 
                    onChange={(e) => setForm({ ...form, email: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input 
                    value={form.phone} 
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                    placeholder="+41 XX XXX XX XX"
                  />
                </div>
              </div>
            </div>

            {/* Adresse */}
            <div>
              <h3 className="font-medium text-slate-700 mb-3">Adresse</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input 
                    value={form.address} 
                    onChange={(e) => setForm({ ...form, address: e.target.value })} 
                    placeholder="Rue et numéro"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input 
                      value={form.postal_code} 
                      onChange={(e) => setForm({ ...form, postal_code: e.target.value })} 
                      placeholder="1200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input 
                      value={form.city} 
                      onChange={(e) => setForm({ ...form, city: e.target.value })} 
                      placeholder="Genève"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Informations professionnelles */}
            <div>
              <h3 className="font-medium text-slate-700 mb-3">Informations professionnelles</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Département</Label>
                  <Input 
                    value={form.department} 
                    onChange={(e) => setForm({ ...form, department: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Poste</Label>
                  <Input 
                    value={form.position} 
                    onChange={(e) => setForm({ ...form, position: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Chantier actuel</Label>
                  <Input 
                    value={form.chantier} 
                    onChange={(e) => setForm({ ...form, chantier: e.target.value })} 
                    placeholder="Nom du chantier"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date d'embauche</Label>
                  <Input 
                    type="date"
                    value={form.hire_date} 
                    onChange={(e) => setForm({ ...form, hire_date: e.target.value })} 
                  />
                </div>
              </div>
            </div>

            {/* Contact d'urgence */}
            <div>
              <h3 className="font-medium text-slate-700 mb-3">Contact d'urgence</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du contact</Label>
                  <Input 
                    value={form.emergency_contact} 
                    onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} 
                    placeholder="Nom et prénom"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone d'urgence</Label>
                  <Input 
                    value={form.emergency_phone} 
                    onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })} 
                    placeholder="+41 XX XXX XX XX"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={form.notes} 
                onChange={(e) => setForm({ ...form, notes: e.target.value })} 
                rows={3}
                placeholder="Informations supplémentaires..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
